import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  IconButton,
  useToast,
  Badge,
  Input,
  Select,
  Flex,
  Text,
  VStack,
  Skeleton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Switch,
  Tooltip,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  SearchIcon,
  ViewIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataSuperAdmin,
  deleteDataSuperAdmin,
  updateDataSuperAdmin,
} from "../../redux/actions/common.action";
import Pagination from "../proposal/components/Pagination ";
import { ArrowLeft, Filter, Home, Menu, Package, RefreshCcw } from "lucide-react";

const NavbarContentList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { navbarContents, loading, page } = useSelector(
    (state: any) => state.common
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [deleteContentId, setDeleteContentId] = useState<string | null>(null);
  const [deleteContentTitle, setDeleteContentTitle] = useState<string>("");
  const [togglingStatus, setTogglingStatus] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [currentPage, filterSection, pageLimit]);

  const fetchData = () => {
    let query = `navbar-content?page=${currentPage}&limit=${pageLimit}`;
    if (filterSection) query += `&section=${filterSection}`;
    if (searchTerm) query += `&search=${searchTerm}`;

    dispatch(getDataSuperAdmin(query) as any);
  };

  // const handleSearch = () => {
  //   setCurrentPage(1);
  //   fetchData();
  // };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchData();
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleDeleteClick = (id: string, title: string) => {
    setDeleteContentId(id);
    setDeleteContentTitle(title);
    onOpen();
  };

  const confirmDelete = async () => {
    if (deleteContentId) {
      try {
        await dispatch(
          deleteDataSuperAdmin(deleteContentId, "navbar-content", toast) as any
        );
        fetchData();
        onClose();
        setDeleteContentId(null);
        setDeleteContentTitle("");
      } catch (error) {
        console.error("Error deleting content:", error);
      }
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    setTogglingStatus(id);
    try {
      await dispatch(
        updateDataSuperAdmin(
          id,
          "navbar-content",
          { isActive: !currentStatus },
          navigate,
          toast
        ) as any
      );
      fetchData();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast({
        title: "Error",
        description: "Failed to update content status",
        status: "error",
        position: "top-right",
        isClosable: true,
      });
    } finally {
      setTogglingStatus(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageLimitChange = (newLimit: number) => {
    setPageLimit(newLimit);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterSection("");
    setCurrentPage(1);
    fetchData();
  };

  const getSectionColor = (section: string) => {
    const colors: any = {
      features: "blue",
      industries: "purple",
      resources: "green",
    };
    return colors[section.toLowerCase()] || "gray";
  };

  const getPaginationInfo = () => {
    if (!navbarContents || navbarContents.length === 0 || !page?.total) {
      return "Showing 0 to 0 of 0 contents";
    }
    const startItem = (currentPage - 1) * pageLimit + 1;
    const endItem = Math.min(currentPage * pageLimit, page.total);
    return `Showing ${startItem} to ${endItem} of ${page.total} contents`;
  };

  return (
    <Box p="8px" bg="gray.50">
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .refresh-icon-spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>

      {/* Modern Back Navigation Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        px={{ base: 3, md: 4 }}
        py={{ base: 2, md: 3 }}
        className="slide-in-left"
      >
        <Flex
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={{ base: 2, md: 4 }}
        >
          {/* Left: Back Button + Breadcrumb */}
          <Flex
            flexDirection={{ base: "row", lg: "column" }}
            align={{ base: "center", lg: "flex-start" }}
            gap={{ base: 2, lg: 1 }}
          >
            {/* Responsive Back Button */}
            <Button
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate("/crm-super-admin-dashboard")}
              size={{ base: "xs", sm: "sm" }}
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize={{ base: "xs", sm: "sm" }}
              px={3}
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              {/* Hide text on small screens */}
              <Box display={{ base: "none", sm: "inline" }}>
                Back to Dashboard
              </Box>
            </Button>

            {/* Breadcrumb Navigation */}
            <Breadcrumb
              spacing={2}
              separator={<ChevronRightIcon color="gray.400" boxSize={3} />}
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.600"
              display={{ base: "none", sm: "flex" }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => navigate("/crm-super-admin-dashboard")}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Dashboard</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <Menu size={12} />
                  <Text>Legal Content</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </Flex>

          {/* Right: Page Title and Stats */}
          <HStack spacing={{ base: 2, md: 4 }} align="center">
            <VStack align="end" spacing={0}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="bold"
                color="gray.800"
              >
                Legal Contents
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                Manage Legal Contents – Total {navbarContents?.length}
              </Text>
            </VStack>

            <Box
              bgGradient="linear(to-br, purple.400, purple.600)"
              p={{ base: 2, md: 3 }}
              borderRadius="xl"
              boxShadow="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Menu size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
      >
        <HStack mt={4} px={4} pb={4} justify="space-between" align="center">
          <Box position="relative" maxW="md">
            <SearchIcon
              position="absolute"
              left="12px"
              top="50%"
              transform="translateY(-50%)"
              color="gray.400"
              w={4}
              h={4}
            />
            <Input
              w={"400px"}
              type="text"
              placeholder="Search by section, sub-section, or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              // onKeyPress={(e) => handleSearch()}
              pl="40px"
              pr={4}
              py={2}
              fontSize="xs"
              border="1px"
              borderColor="gray.300"
              borderRadius="lg"
              _focus={{
                outline: "none",
                ring: 1,
                ringColor: "blue.500",
                borderColor: "blue.500",
              }}
            />
          </Box>

          <HStack spacing={2}>
            <Button
              size="sm"
              leftIcon={<AddIcon w={3} h={3} />}
              bg="blue.500"
              color="white"
              _hover={{ bg: "blue.600" }}
              onClick={() => navigate("/navbar-content/add")}
              fontSize="sm"
              px={4}
              py="4px"
              borderRadius="lg"
            >
              Create Content
            </Button>
            <Button
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
              px={3}
              py={2}
              borderRadius="lg"
              leftIcon={<Filter size={12} />}
            >
              Filters
            </Button>
            <Button
              size="sm"
              onClick={handleClearFilters}
              isDisabled={loading}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={!loading ? { bg: "gray.50" } : {}}
              px={3}
              py={2}
              borderRadius="lg"
              title="Refresh data and clear filters"
            >
              <Box
                as={RefreshCcw}
                size={14}
                mr={2}
                className={loading ? "refresh-icon-spin" : ""}
              />
              Refresh
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Box
          p={4}
          bg="gray.50"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          mb="8px"
        >
          <HStack spacing={3}>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                Section
              </Text>
              <Select
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
                fontSize="xs"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={2}
                py={1}
                size="sm"
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                }}
              >
                <option value="">All Sections</option>
                <option value="features">Features</option>
                <option value="industries">Industries</option>
                <option value="resources">Resources</option>
              </Select>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Table */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50" borderBottom="1px" borderColor="gray.200">
              <Tr>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Section
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Sub-section
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Title
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Sections Count
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Status
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody borderTop="1px" borderColor="gray.200">
              {loading ? (
                [...Array(pageLimit)].map((_, index) => (
                  <Tr key={index}>
                    {[...Array(6)].map((_, cellIndex) => (
                      <Td key={cellIndex} px={3} py={3}>
                        <Skeleton height="16px" borderRadius="md" />
                      </Td>
                    ))}
                  </Tr>
                ))
              ) : navbarContents?.length > 0 ? (
                navbarContents.map((content: any) => (
                  <Tr
                    key={content._id}
                    _hover={{ bg: "gray.50" }}
                    borderBottom="1px"
                    borderColor="gray.200"
                  >
                    <Td px={3} py={3}>
                      <Badge
                        colorScheme={getSectionColor(content.section)}
                        fontSize="xs"
                        px={2}
                        py={1}
                        borderRadius="full"
                        fontWeight="medium"
                      >
                        {content.section.toUpperCase()}
                      </Badge>
                    </Td>
                    <Td px={3} py={3}>
                      <Text fontSize="xs" fontWeight="medium" color="gray.900">
                        {content.subSection}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <Text fontSize="xs" fontWeight="medium" color="gray.900">
                        {content.title}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <Badge
                        colorScheme="gray"
                        fontSize="xs"
                        px={2}
                        py={1}
                        borderRadius="md"
                      >
                        {content.pageSections?.length || 0}
                      </Badge>
                    </Td>
                    <Td px={3} py={3}>
                      <Tooltip
                        label={
                          content.isActive
                            ? "Click to deactivate"
                            : "Click to activate"
                        }
                        placement="top"
                      >
                        <HStack spacing={2}>
                          <Switch
                            size="sm"
                            colorScheme="green"
                            isChecked={content.isActive}
                            isDisabled={togglingStatus === content._id}
                            onChange={() =>
                              handleToggleStatus(content._id, content.isActive)
                            }
                          />
                          <Badge
                            colorScheme={content.isActive ? "green" : "red"}
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="full"
                            fontWeight="medium"
                          >
                            {content.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </HStack>
                      </Tooltip>
                    </Td>
                    <Td px={3} py={3}>
                      <HStack spacing={1}>
                        <IconButton
                          aria-label="View"
                          icon={<ViewIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() =>
                            navigate(`/navbar-content/view/${content._id}`)
                          }
                          p="6px"
                          minW="auto"
                          h="auto"
                          _hover={{ bg: "blue.50" }}
                          title="View Details"
                        />
                        <IconButton
                          aria-label="Edit"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() =>
                            navigate(`/navbar-content/edit/${content._id}`)
                          }
                          p="6px"
                          minW="auto"
                          h="auto"
                          _hover={{ bg: "orange.50" }}
                          title="Edit Content"
                        />
                        <IconButton
                          aria-label="Delete"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() =>
                            handleDeleteClick(content._id, content.title)
                          }
                          p="6px"
                          minW="auto"
                          h="auto"
                          _hover={{ bg: "red.50" }}
                          title="Delete Content"
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={6} px={3} py={16} textAlign="center">
                    <VStack spacing={4}>
                      <Package size={64} color="#d1d5db" />
                      {searchTerm || filterSection ? (
                        <VStack spacing={2}>
                          <Text
                            fontSize="lg"
                            fontWeight="medium"
                            color="gray.700"
                          >
                            No contents found
                          </Text>
                          <VStack spacing={2}>
                            <Text fontSize="sm" color="gray.500">
                              No results match your search criteria
                            </Text>
                            <HStack spacing={2}>
                              <Button
                                onClick={() => {
                                  setSearchTerm("");
                                  fetchData();
                                }}
                                size="xs"
                                variant="link"
                                colorScheme="blue"
                              >
                                Clear search
                              </Button>
                              <Text fontSize="xs" color="gray.400">
                                or
                              </Text>
                              <Button
                                onClick={handleClearFilters}
                                size="xs"
                                variant="link"
                                colorScheme="blue"
                              >
                                Reset all filters
                              </Button>
                            </HStack>
                          </VStack>
                        </VStack>
                      ) : (
                        <VStack spacing={6}>
                          <Text
                            fontSize="lg"
                            fontWeight="medium"
                            color="gray.700"
                          >
                            No navbar content yet
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            Create your first content to get started
                          </Text>
                          <Button
                            onClick={() => navigate("/navbar-content/add")}
                            bg="indigo.600"
                            color="white"
                            _hover={{ bg: "indigo.700" }}
                            size="sm"
                            leftIcon={<Package size={16} />}
                          >
                            Create Navbar Content
                          </Button>
                        </VStack>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        <Flex justify="space-between" align="center" px={2} py={2}>
          <HStack spacing={4}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {getPaginationInfo()}
            </Text>
          </HStack>

          <HStack spacing={4}>
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">
                Show
              </Text>
              <Select
                value={pageLimit}
                onChange={(e) =>
                  handlePageLimitChange(parseInt(e.target.value))
                }
                size="xs"
                bg="white"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={3}
                py={1}
                fontSize="xs"
                fontWeight="medium"
                color="gray.700"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                  borderColor: "blue.500",
                }}
                w="100px"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <Text fontSize="xs" color="gray.600">
                per page
              </Text>
            </HStack>

            {page?.totalPages > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={page.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md" fontWeight="semibold">
            Delete Content
          </ModalHeader>
          <ModalBody>
            <VStack align="start" spacing={3}>
              <Text fontSize="sm" color="gray.700">
                Are you sure you want to delete this content?
              </Text>
              <Box
                bg="red.50"
                p={3}
                borderRadius="md"
                border="1px"
                borderColor="red.200"
                w="full"
              >
                <Text fontSize="sm" fontWeight="semibold" color="red.800">
                  {deleteContentTitle}
                </Text>
              </Box>
              <Text fontSize="xs" color="gray.500">
                This action will soft delete the content. It will be hidden from
                the public but can be restored by an administrator if needed.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                fontSize="xs"
              >
                Cancel
              </Button>
              <Button
                isDisabled={loading}
                colorScheme="red"
                size="sm"
                onClick={confirmDelete}
                fontSize="xs"
              >
                {loading ? "Deleting..." : "Delete"}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default NavbarContentList;
